/**
 * Canonical string keys for `bets` + merge accidental duplicate rows (same game id).
 * Prevents split state when some paths use numeric ids and others use strings.
 * @param {Record<string, unknown>} bets
 * @returns {Record<string, object>}
 */
export function normalizeBetsMap(bets) {
  if (!bets || typeof bets !== 'object') return {};
  const out = {};
  for (const [k, row] of Object.entries(bets)) {
    if (!row || typeof row !== 'object') continue;
    const sk = String(k);
    if (!out[sk]) {
      out[sk] = cloneBetRow(row);
    } else {
      out[sk] = mergeBetRows(out[sk], row);
    }
  }
  return out;
}

function cloneBetRow(row) {
  const r = { ...row };
  if (row.clashAugments && typeof row.clashAugments === 'object') {
    r.clashAugments = { ...row.clashAugments };
  } else {
    delete r.clashAugments;
  }
  return r;
}

function mergeBetRows(a, b) {
  const fromA = a.clashAugments && typeof a.clashAugments === 'object' ? a.clashAugments : {};
  const fromB = b.clashAugments && typeof b.clashAugments === 'object' ? b.clashAugments : {};
  const ca = { ...fromA, ...fromB };
  const cleaned = {};
  for (const [ck, cv] of Object.entries(ca)) {
    if (cv != null && cv !== '') cleaned[ck] = cv;
  }
  return {
    predictedHomeScore: a.predictedHomeScore ?? b.predictedHomeScore ?? 0,
    predictedAwayScore: a.predictedAwayScore ?? b.predictedAwayScore ?? 0,
    ...(a.augment != null ? { augment: a.augment } : b.augment != null ? { augment: b.augment } : {}),
    ...(a.points != null ? { points: a.points } : b.points != null ? { points: b.points } : {}),
    ...(Object.keys(cleaned).length > 0 ? { clashAugments: cleaned } : {})
  };
}

/**
 * Where a clash augment for `augmentMapKey` is stored in the round bets map.
 * @param {Record<string, { clashAugments?: Record<string, string> }>} bets
 * @param {string} augmentMapKey
 * @returns {{ gameId: string, code: string } | null}
 */
export function findPlacementForClashAugment(bets, augmentMapKey) {
  if (!bets || !augmentMapKey) return null;
  for (const [gid, b] of Object.entries(bets)) {
    const ca = b?.clashAugments;
    if (!ca || typeof ca !== 'object') continue;
    if (!Object.prototype.hasOwnProperty.call(ca, augmentMapKey)) continue;
    const val = ca[augmentMapKey];
    if (val == null || val === '') continue;
    return { gameId: String(gid), code: String(val) };
  }
  return null;
}

/** Clash augment code stored on a specific match row (same opponent key may appear on several games). */
export function getClashAugmentCodeOnGame(bets, gameId, augmentMapKey) {
  if (!bets || augmentMapKey == null || augmentMapKey === '') return null;
  const gid = String(gameId);
  const ca = bets[gid]?.clashAugments;
  if (!ca || typeof ca !== 'object') return null;
  if (!Object.prototype.hasOwnProperty.call(ca, augmentMapKey)) return null;
  const val = ca[augmentMapKey];
  if (val == null || val === '') return null;
  return String(val);
}

/** First other match (not `excludeGameId`) that uses this augment type for the same clash key. */
export function findElsewhereClashAugmentForType(bets, augmentMapKey, excludeGameId, code) {
  if (!bets || augmentMapKey == null || augmentMapKey === '' || code == null || code === '') return null;
  const ex = String(excludeGameId);
  const want = String(code);
  for (const [gid, b] of Object.entries(bets)) {
    if (String(gid) === ex) continue;
    const ca = b?.clashAugments;
    if (!ca || typeof ca !== 'object') continue;
    if (!Object.prototype.hasOwnProperty.call(ca, augmentMapKey)) continue;
    const val = ca[augmentMapKey];
    if (val == null || val === '') continue;
    if (String(val) !== want) continue;
    return { gameId: String(gid), code: want };
  }
  return null;
}
